// packages/web/src/pages/PlayerSchedulePage.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Avatar,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { addDays, format, addMinutes, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, startOfWeek, endOfWeek, isToday, isWithinInterval } from 'date-fns';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { PlayerSlot, User, TimeRange, Court, PlayerSlotService, AuthService, Filters, FirebaseService, formatPlayerRatings, Challenge, ChallengeService, formatTimeRange, formatDate } from '@matchpoint/shared';
import { useAuth } from '../hooks/useAuth';

// Styled components matching landing page design
const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#fafafa',
});

const ProfileContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  minHeight: 'calc(100vh - 200px)',
  maxWidth: '1200px !important',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const HeroSection = styled(Paper)(({ theme }) => ({
  backgroundColor: '#b3c200',
  color: '#1a1a1a',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.25),
  },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  fontSize: '2rem',
  border: '3px solid rgba(255, 255, 255, 0.5)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#84b817',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: 60,
    height: 60,
    fontSize: '1.5rem',
    border: '3px solid rgba(255, 255, 255, 0.5)',
  },
}));

const ProfileInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const ProfileTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  marginBottom: 0,
  letterSpacing: '-0.02em',
  lineHeight: 1.3,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.125rem',
    lineHeight: 1.3,
  },
}));

const ProfileSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  opacity: 0.85,
  marginBottom: 0,
  fontWeight: 400,
  lineHeight: 1.4,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    lineHeight: 1.4,
  },
}));

const StatsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  fontSize: '0.875rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    gap: theme.spacing(0.25),
  },
}));

const StatItem = styled(Box)(({ theme }) => ({
  display: 'inline',
  fontWeight: 400,
  '& .stat-label': {
    opacity: 0.85,
  },
}));

const RatingBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: '#2e7d32',
  color: '#ffffff',
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '0.813rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    padding: '3px 10px',
  },
}));

const ContentSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  marginBottom: theme.spacing(2.5),
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#1a1a1a',
  marginBottom: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  letterSpacing: '-0.01em',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1),
  },
}));

const CalendarWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.5),
  },
}));

const CalendarSection = styled(Paper)(({ theme }) => ({
  flex: '0 0 auto',
  width: 'auto',
  padding: theme.spacing(2),
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.5),
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.25),
  },
}));

const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  '& .MuiTypography-root': {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: '#1a1a1a',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
    '& .MuiTypography-root': {
      fontSize: '0.95rem',
    },
  },
}));

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.25),
  },
}));

const WeekDayHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  fontWeight: 600,
  fontSize: '0.8rem',
  color: '#666',
  padding: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    padding: theme.spacing(0.25),
  },
}));

const CalendarDay = styled(Box, {
  shouldForwardProp: (prop) => !['isSelected', 'isToday', 'hasSlots', 'isCurrentMonth'].includes(prop as string),
})<{
  isSelected: boolean;
  isToday: boolean;
  hasSlots: boolean;
  isCurrentMonth: boolean;
}>(({ theme, isSelected, isToday, hasSlots, isCurrentMonth }) => ({
  aspectRatio: '1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: '8px',
  border: '1px solid transparent',
  position: 'relative',
  backgroundColor: isSelected ? '#c4d600' : isToday ? '#f0f8e8' : '#ffffff',
  color: isSelected ? '#1a1a1a' : isCurrentMonth ? '#1a1a1a' : '#999',
  fontWeight: isToday || isSelected ? 700 : 400,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: isSelected ? '#b3c200' : '#fafafa',
    borderColor: '#c4d600',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '4px',
  },
}));

const SlotIndicator = styled(Box)(({ theme }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: '#84b817',
  position: 'absolute',
  bottom: 4,
  [theme.breakpoints.down('sm')]: {
    width: 4,
    height: 4,
    bottom: 2,
  },
}));

const DayDetailsSection = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: '#ffffff',
  overflowY: 'auto',
  maxHeight: '600px',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.5),
    maxHeight: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.25),
  },
}));

const SlotCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: '12px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  transition: 'all 0.2s ease',
  backgroundColor: '#ffffff',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    borderColor: '#c4d600',
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.25),
    borderRadius: '8px',
  },
}));

const ChallengeButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontSize: '0.95rem',
  fontWeight: 700,
  padding: theme.spacing(1.25, 2.5),
  borderRadius: '8px',
  textTransform: 'none',
  border: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 4px 12px rgba(196, 214, 0, 0.3)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#999',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    padding: theme.spacing(1, 2),
    fontWeight: 600,
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  fontSize: '0.95rem',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  borderRadius: '8px',
  border: '2px solid #e5e5e5',
  textTransform: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#c4d600',
    backgroundColor: '#fafafa',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    padding: theme.spacing(0.875, 2),
    fontWeight: 600,
  },
}));

const PendingChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#fff3cd',
  color: '#856404',
  fontWeight: 600,
  borderRadius: '6px',
  fontSize: '0.75rem',
}));

const AcceptedChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#d4edda',
  color: '#155724',
  fontWeight: 600,
  borderRadius: '6px',
  fontSize: '0.75rem',
}));

const PlayerSchedulePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, signOut } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const [playerSlots, setPlayerSlots] = useState<PlayerSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date()); // Auto-select today
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [pendingChallenges, setPendingChallenges] = useState<Challenge[]>([]);
  
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<PlayerSlot | null>(null);
  const [challengeMessage, setChallengeMessage] = useState('');
  const [challengeError, setChallengeError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const playerSlotService = PlayerSlotService.getInstance();
  const challengeService = ChallengeService.getInstance();
  const authService = AuthService.getInstance();

  // Compute visible slots based on user login status and challenges
  const visibleSlots = React.useMemo(() => {
    if (!user) {
      // Non-logged-in users see all available slots (already filtered by loadUserData)
      return playerSlots;
    }
    
    // Logged-in users: show slots they've interacted with OR available slots
    return playerSlots.filter(slot => {
      const slotData = slot as any;
      const userChallenge = pendingChallenges.find(c => c.slotId === slot.id);
      
      // Show slot if user has interacted with it
      if (userChallenge) {
        return true;
      }
      
      // For slots user hasn't interacted with, only show if available
      return !(slotData.booked === true || slotData.status === 'booked' || slotData.status === 'unavailable');
    });
  }, [playerSlots, user, pendingChallenges]);

  useEffect(() => {
    loadUserData();
  }, [userId]);

  useEffect(() => {
    if (user) {
      loadPendingChallenges();
    }
  }, [user, userId]);

  const loadUserData = async () => {
    if (!userId) {
      setError('User ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Load user profile using AuthService
      const userProfile = await authService.getPublicProfile(userId);
      
      if (!userProfile) {
        setError('User not found');
        setLoading(false);
        return;
      }
      
      setTargetUser(userProfile);

      // Load shared calendar slots (public slots only)
      const allSlots = await playerSlotService.getSharedCalendarSlots(userId);
      
      // For non-logged-in users: only show available slots
      let filteredSlots = allSlots;
      if (!user) {
        filteredSlots = await playerSlotService.filterAvailableSlots(allSlots);
      }
      // For logged-in users, we'll filter in the render based on pendingChallenges
      
      setPlayerSlots(filteredSlots);

      if (filteredSlots.length > 0) {
        setSelectedDate(filteredSlots[0].startTime);
      } else {
        // Set to current date if no slots
        setSelectedDate(new Date());
      }
    } catch (err: any) {
      console.error('Error loading user data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingChallenges = async () => {
    if (!user || !userId) return;

    try {
      const challenges = await challengeService.getSentChallenges(user.uid);
      // Filter challenges where the slotId matches any of the user's slots
      const userSlotIds = playerSlots.map(slot => slot.id);
      const userChallenges = challenges.filter((c: Challenge) => 
        c.toUserId === userId || userSlotIds.includes(c.slotId)
      );
      setPendingChallenges(userChallenges);
    } catch (err) {
      console.error('Error loading challenges:', err);
    }
  };

  const handleChallengeClick = (slot: PlayerSlot) => {
    if (!user) {
      setLoginDialogOpen(true);
      return;
    }

    setSelectedSlot(slot);
    setChallengeMessage(`Hi ${slot.playerName}! I'd like to play tennis with you.`);
    setChallengeError('');
    setChallengeDialogOpen(true);
  };

  const handleCloseChallengeDialog = () => {
    setChallengeDialogOpen(false);
    setSelectedSlot(null);
    setChallengeMessage('');
    setChallengeError('');
  };

  const handleSendChallenge = async () => {
    if (!selectedSlot || !user) return;

    setIsSubmitting(true);
    setChallengeError('');

    try {
      await challengeService.createChallenge(
        user,
        selectedSlot.playerId,
        selectedSlot.playerName,
        selectedSlot,
        challengeMessage
      );

      handleCloseChallengeDialog();
      await loadPendingChallenges();
    } catch (err: any) {
      setChallengeError(err.message || 'Failed to send challenge');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSlotCard = (slot: PlayerSlot) => {
    const userChallenge = pendingChallenges.find(c => c.slotId === slot.id);
    const isAccepted = userChallenge?.status === 'accepted';
    const isPending = userChallenge?.status === 'pending';
    const canChallenge = !userChallenge || userChallenge.status === 'rejected';
    const isOwnSlot = user?.uid === slot.playerId; // Check if this is user's own slot

    return (
      <SlotCard key={slot.id}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.125rem' },
                fontWeight: 700,
                color: '#1a1a1a'
              }}>
                <AccessTimeIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#84b817' }} />
                {formatTimeRange(
                  slot.startTime,
                  new Date(slot.startTime.getTime() + slot.duration * 60000)
                )}
              </Typography>
              
              <Typography variant="body2" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 0.5,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: '#666'
              }}>
                <LocationOnIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                {slot.court}, {slot.city}
                {slot.country && `, ${slot.country}`}
              </Typography>
              
              {slot.playerRatings && (
                <Typography variant="caption" sx={{ 
                  display: 'block', 
                  mt: 1,
                  color: '#666',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}>
                  🎾 Level: {formatPlayerRatings(slot.playerRatings)}
                </Typography>
              )}
            </Box>
            
            {isAccepted && (
              <AcceptedChip
                icon={<CheckCircleIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                label="Accepted"
                size="small"
              />
            )}
            
            {isPending && (
              <PendingChip
                label="Pending"
                size="small"
              />
            )}
          </Box>
          
          {!isOwnSlot && canChallenge && (
            <ChallengeButton
              fullWidth
              onClick={() => handleChallengeClick(slot)}
              sx={{ mt: 1.5 }}
            >
              Challenge Player
            </ChallengeButton>
          )}
        </CardContent>
      </SlotCard>
    );
  };

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  if (loading) {
    return (
      <PageContainer>
        <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh' 
        }}>
          <CircularProgress sx={{ color: '#c4d600' }} />
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
        <ProfileContainer>
          <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>
        </ProfileContainer>
      </PageContainer>
    );
  }

  if (!targetUser) {
    return (
      <PageContainer>
        <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
        <ProfileContainer>
          <Alert severity="info" sx={{ borderRadius: '8px' }}>User not found</Alert>
        </ProfileContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
      
      <ProfileContainer>
        {/* Hero Section */}
        <HeroSection elevation={0}>
          <ProfileHeader>
            <ProfileAvatar src={targetUser.profilePicture}>
              {targetUser.name ? targetUser.name.charAt(0).toUpperCase() : 'U'}
            </ProfileAvatar>
            <ProfileInfo>
              <ProfileTitle>
                {targetUser.name || 'Tennis Player'}
              </ProfileTitle>
              <StatsSection>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {targetUser.preferences?.ratings && (
                    <RatingBadge>
                      UTR: {targetUser.preferences.ratings.utr?.toFixed(1) || 'N/A'} • NTRP: {targetUser.preferences.ratings.ntrp?.toFixed(1) || 'N/A'}
                    </RatingBadge>
                  )}
                </Box>
                <Box sx={{ opacity: 0.85 }}>
                  {visibleSlots.length} slot{visibleSlots.length !== 1 ? 's' : ''} • {targetUser.preferences?.timezone || 'UTC'}
                </Box>
              </StatsSection>
            </ProfileInfo>
          </ProfileHeader>
        </HeroSection>

        {/* Calendar Section */}
        <ContentSection elevation={0}>
          <SectionTitle>
            <AccessTimeIcon sx={{ color: '#84b817' }} />
            Available Time Slots
          </SectionTitle>
          
          {visibleSlots.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                No available slots
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                This player hasn't published any availability yet
              </Typography>
            </Box>
          ) : (
            <CalendarWrapper>
              {/* Calendar Section */}
              <CalendarSection elevation={0}>
                <CalendarHeader>
                  <IconButton 
                    onClick={() => setCurrentMonth(addDays(currentMonth, -30))}
                    size="small"
                    sx={{ padding: { xs: '4px', sm: '8px' } }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    color: '#1a1a1a'
                  }}>
                    {format(currentMonth, 'MMMM yyyy')}
                  </Typography>
                  <IconButton 
                    onClick={() => setCurrentMonth(addDays(currentMonth, 30))}
                    size="small"
                    sx={{ padding: { xs: '4px', sm: '8px' } }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </CalendarHeader>
                
                {/* Week day headers */}
                <CalendarGrid sx={{ marginBottom: 1 }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <WeekDayHeader key={day}>
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{day}</Box>
                      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>{day.charAt(0)}</Box>
                    </WeekDayHeader>
                  ))}
                </CalendarGrid>
                
                {/* Calendar days */}
                <CalendarGrid>
                  {(() => {
                    const monthStart = startOfMonth(currentMonth);
                    const monthEnd = endOfMonth(monthStart);
                    const startDate = startOfWeek(monthStart);
                    const endDate = endOfWeek(monthEnd);
                    const days = eachDayOfInterval({ start: startDate, end: endDate });
                    
                    return days.map(day => {
                      const daySlots = visibleSlots.filter(slot => 
                        isSameDay(slot.startTime, day)
                      );
                      const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                      
                      return (
                        <CalendarDay
                          key={day.toISOString()}
                          isSelected={selectedDate ? isSameDay(day, selectedDate) : false}
                          isToday={isToday(day)}
                          hasSlots={daySlots.length > 0}
                          isCurrentMonth={isCurrentMonth}
                          onClick={() => setSelectedDate(day)}
                        >
                          <Typography variant="body2" sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            lineHeight: 1
                          }}>
                            {format(day, 'd')}
                          </Typography>
                          {daySlots.length > 0 && <SlotIndicator />}
                        </CalendarDay>
                      );
                    });
                  })()}
                </CalendarGrid>
              </CalendarSection>
              
              {/* Day Details Section */}
              <DayDetailsSection elevation={0}>
                {selectedDate && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h5" gutterBottom sx={{ 
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        fontWeight: 700,
                        color: '#1a1a1a',
                        mb: 0.5
                      }}>
                        {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.8rem' } 
                      }}>
                        {(() => {
                          const daySlots = visibleSlots.filter(slot => 
                            isSameDay(slot.startTime, selectedDate)
                          );
                          const availableCount = daySlots.filter(slot => {
                            const userChallenge = pendingChallenges.find(c => c.slotId === slot.id);
                            return !userChallenge || userChallenge.status !== 'accepted';
                          }).length;
                          return `${availableCount} available slot${availableCount !== 1 ? 's' : ''}`;
                        })()}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {(() => {
                      const daySlots = visibleSlots
                        .filter(slot => isSameDay(slot.startTime, selectedDate))
                        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
                      
                      if (daySlots.length === 0) {
                        return (
                          <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ 
                              fontSize: { xs: '0.8rem', sm: '0.9rem' } 
                            }}>
                              No slots scheduled for this day
                            </Typography>
                          </Box>
                        );
                      }
                      
                      return daySlots.map(renderSlotCard);
                    })()}
                  </>
                )}
              </DayDetailsSection>
            </CalendarWrapper>
          )}
        </ContentSection>
      </ProfileContainer>
      
      <Footer />
      
      {/* Challenge Dialog */}
      <Dialog 
        open={challengeDialogOpen} 
        onClose={handleCloseChallengeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: '1px solid #e5e5e5',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1rem', sm: '1.25rem' },
          fontWeight: 700,
          color: '#1a1a1a'
        }}>
          Challenge {selectedSlot?.playerName}
        </DialogTitle>
        <DialogContent>
          {challengeError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {challengeError}
            </Alert>
          )}
          
          <DialogContentText sx={{ 
            mb: 2, 
            fontSize: { xs: '0.875rem', sm: '1rem' },
            color: '#666'
          }}>
            You're challenging {selectedSlot?.playerName} for a match on{' '}
            {selectedSlot && formatDate(selectedSlot.startTime)} at{' '}
            {selectedSlot && formatTimeRange(
              selectedSlot.startTime,
              new Date(selectedSlot.startTime.getTime() + selectedSlot.duration * 60000)
            )}.
          </DialogContentText>
          
          <Box sx={{ 
            mb: 2, 
            p: { xs: 1.5, sm: 2 }, 
            bgcolor: '#fafafa', 
            borderRadius: '8px',
            border: '1px solid #e5e5e5'
          }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              color: '#1a1a1a'
            }}>
              Match Details:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: '#666' }}>
              📍 {selectedSlot?.court}, {selectedSlot?.city}
              {selectedSlot?.country ? `, ${selectedSlot.country}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: '#666' }}>
              ⏱️ Duration: {selectedSlot?.duration} minutes
            </Typography>
            {selectedSlot?.playerRatings && (
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: '#666' }}>
                🎾 Player Level: {formatPlayerRatings(selectedSlot.playerRatings)}
              </Typography>
            )}
          </Box>
          
          <TextField
            autoFocus
            margin="dense"
            label="Personal Message"
            fullWidth
            multiline
            rows={4}
            value={challengeMessage}
            onChange={(e) => setChallengeMessage(e.target.value)}
            disabled={isSubmitting}
            placeholder="Add a personal message to your challenge..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#fafafa',
                '& fieldset': {
                  borderColor: '#e5e5e5',
                },
                '&:hover fieldset': {
                  borderColor: '#c4d600',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#c4d600',
                  borderWidth: '2px',
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <SecondaryButton 
            onClick={handleCloseChallengeDialog} 
            disabled={isSubmitting}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Cancel
          </SecondaryButton>
          <ChallengeButton 
            onClick={handleSendChallenge} 
            disabled={isSubmitting}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Send Challenge'}
          </ChallengeButton>
        </DialogActions>
      </Dialog>
      
      {/* Login Dialog */}
      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </PageContainer>
  );
};

export default PlayerSchedulePage;